import _ from 'lodash';
import React, { useEffect } from 'react';
import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducers';
import { EnvironmentVariables, WppBreadcrumbCustomEvent, BreadcrumbItemEventDetails, Strategy } from 'utils/types';
import { WppBreadcrumb, WppGrid, WppIconChevron, WppTypography } from 'buildingBlocks';
import Strategy403 from 'containers/403/components/Strategy403';
import { WidgetWrapper, clearAllWidgetFieldsBesidesEmail } from 'utils/widget';
import PageLoading from 'components/PageLoading';
import PageLoadError from 'components/PageLoadError';
import { TabView } from 'components/WppTabs';
import { TabType } from 'components/WppTabs/TabView';
import { stratHasRevenueType } from 'charts/InsightsViz/utils';
import { checkPermissions, Permission } from 'utils/featureFlags';
import UnknownError from 'containers/UnknownError/components/UnknownError';
import WppPermissionPageTemplate from 'components/PageTemplate/WppPermissionPageTemplate';
import WppDeleteConfirmationModal from 'components/WppDeleteConfirmationModal';
import { fetchStrategy, resetProps, toggleDeleteModal, deleteStrategy } from './actions';
import Metadata from './components/Metadata';
import { TABS, overviewTab, INSIGHTS_TAB_LABEL, DATA_UPLOADER_TAB_LABEL } from './utils/tabUtils';
import { SYSTEM_ERROR_STATUS_CODE } from './components/View/DataUploader/constants';

const PAGE_NAME = 'Strategy Analytics';

const Loading = ({ id }: { id: string }) => (
  <PageLoading pageName={PAGE_NAME}>{`Loading data for strategy [${id}]`}</PageLoading>
);

const DataProcessing = ({ id, name }: { id: string; name: string }) => (
  <PageLoading pageName={PAGE_NAME}>
    {`Processing data for strategy [${name} (${id})]`}
  </PageLoading>
);

const CustomHeader: React.FC = () => {
  const navigate = useNavigate();
  return (
    <div>
      <div className="backIconStyle">
        <WppIconChevron direction="left" size="s" />
        <WppBreadcrumb
          items={[{ label: 'Back', path: '/strategies' }]}
          onWppChange={(event: WppBreadcrumbCustomEvent<BreadcrumbItemEventDetails>) => navigate(event.detail.path)}
        />
      </div>
      <WppTypography tag="p" type="3xl-heading">Strategy Analytics</WppTypography>
    </div>
  );
};

const StrategyAnalytics: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id: strategyId } = useParams<{ id: string }>();
  const strategyAnalytics = useSelector((state: RootState) => state.strategyAnalytics);
  const user = useSelector((state: RootState) => state.login.session.data.user);
  const env = useSelector((state: RootState) => state.login.env);

  const {
    metadata: {
      strategy,
      strategyGoalAnalyticsMetadata: { goal: strategyGoal },
      loading,
      processingData,
      error,
      status,
      isDeleteModalOpen,
      deletionFailed,
      flights,
    },
  } = strategyAnalytics;

  const hasFlights = !!_.size(flights);
  const shouldRenderPage = !_.some([loading, processingData, error]);

  const fetchStrategyData = () => {
    if (strategyId) {
      dispatch(fetchStrategy(strategyId));
      WidgetWrapper('prefill', 'ticketForm', { custom_fields: { cf_copilot_strategy_id: strategyId } });
    }
  };

  useEffect(() => {
    fetchStrategyData();

    return () => {
      dispatch(resetProps());
      clearAllWidgetFieldsBesidesEmail(user.email);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, strategyId, user.email]);

  const getTabs = () => {
    const strategyTypeId = _.get(strategy, 'strategyType.id');
    const strategyEquation = _.get(strategyGoal, 'equation');
    const includeDataUploader = _.includes(strategyEquation, 'externalCustomValue') && checkPermissions(user, Permission.cyodGoalType, strategy.member);
    const baseTabs = _.get(TABS.getById(strategyTypeId), 'tabs', [overviewTab]);
    const hasInsightsPermission = checkPermissions(user, Permission.insightsViz, strategy.member);
    const hasCustomRevenueType = stratHasRevenueType(strategy);
    const includeInsightsTab = env === EnvironmentVariables.demo
      ? hasInsightsPermission && !hasCustomRevenueType && hasFlights
      : hasInsightsPermission && hasFlights;
    const excludedTabLabels = [
      ...(includeDataUploader ? [] : [DATA_UPLOADER_TAB_LABEL]),
      ...(includeInsightsTab ? [] : [INSIGHTS_TAB_LABEL]),
    ];

    return _.filter(baseTabs, (t: { label: string }) => !_.includes(excludedTabLabels, t.label));
  };

  const tabs = getTabs() as Array<TabType>;

  const showStrategyError = () => (
    !loading && _.isEqual(_.get(error, 'status'), SYSTEM_ERROR_STATUS_CODE)
      ? (<UnknownError />)
      : (
        <Strategy403
          strategyId={strategyId}
          user={user}
          loadingComponent={<Loading id={strategyId} />}
        />
      )
  );

  const handleDelete = (itemsToDelete: Strategy) => {
    if (itemsToDelete && strategyId) {
      dispatch(deleteStrategy(itemsToDelete, {
        location,
        navigate,
        params: { strategyId },
      }));
    }
  };

  return (
    loading || strategy.active
      ? (
        // @ts-ignore doesnt pass permission
        <WppPermissionPageTemplate
          title={PAGE_NAME}
          name={PAGE_NAME}
          customHeader={<CustomHeader />}
          unauthorized={status === 403}
          unauthorizedProps={{ message: `You are not authorized to see strategy ${strategyId}.` }}
        >
          {loading && <Loading id={strategyId} />}
          {error && <PageLoadError error={`Error while loading strategy (${strategyId})`} pageName={PAGE_NAME} />}
          {processingData && <DataProcessing id={strategyId} name={strategy.name} />}
          {shouldRenderPage && (
            <>
              <WppGrid container fullWidth>
                <WppGrid item all={24}>
                  <Metadata />
                  <TabView id={strategy.id} tabs={tabs} />
                  <WppDeleteConfirmationModal
                    open={isDeleteModalOpen}
                    itemsToDelete={{ [strategy.id]: strategy }}
                    onCancel={() => dispatch(toggleDeleteModal())}
                    onDelete={handleDelete}
                    footerMessage={deletionFailed ? `Strategy ${strategy.id} failed to delete.` : null}
                    modalName="Delete Strategy"
                    deleteItemName="strategy"
                    deleteMessage="The flights attached to this strategy will be deactivated in the DSP and stop buying immediately."
                  />
                </WppGrid>
              </WppGrid>
              <Outlet />
            </>
          )}
        </WppPermissionPageTemplate>
      )
      : showStrategyError()
  );
};

export default StrategyAnalytics;
